/**
 * User Service Hooks
 * React hooks for accessing user management services
 */
import { useCallback, useState } from 'react';
import { useCCMS } from '../useCCMS';
/**
 * Hook to access all user-related services
 * @returns UsersServices object containing all user service instances
 */
export function useUsersServices() {
    const { services } = useCCMS();
    if (!services?.users) {
        throw new Error('User services not available. Ensure CCMSProvider is configured with user services.');
    }
    return services.users;
}
/**
 * Hook to access AdminService
 */
export function useAdminService() {
    return useUsersServices().admin;
}
/**
 * Hook to access UserService
 */
export function useUserService() {
    return useUsersServices().user;
}
/**
 * Hook to access RoleService
 */
export function useRoleService() {
    return useUsersServices().role;
}
/**
 * Hook to access RightService
 */
export function useRightService() {
    return useUsersServices().right;
}
/**
 * Hook to access UserPreferencesService
 */
export function useUserPreferencesService() {
    return useUsersServices().userPreferences;
}
/**
 * Hook to access UserPushDeviceService
 */
export function useUserPushDeviceService() {
    return useUsersServices().userPushDevice;
}
/**
 * Hook to access AuthService
 */
export function useAuthService() {
    return useUsersServices().auth;
}
/**
 * Hook to access AuditService
 */
export function useAuditService() {
    return useUsersServices().audit;
}
/**
 * Hook to access SystemHealthService from Users module
 */
export function useUsersSystemService() {
    return useUsersServices().system;
}
/**
 * Hook to access PresenceAnalyticsService
 */
export function usePresenceAnalyticsService() {
    return useUsersServices().presenceAnalytics;
}
/**
 * Hook to access UserDriverProfileService (Phase 5a — driver licence registry)
 */
export function useUserDriverProfileService() {
    return useUsersServices().userDriverProfile;
}
/**
 * Hook to update current user's avatar
 * @returns Object with updateAvatar function and mutation state
 */
export function useUpdateMyAvatar() {
    const userService = useUserService();
    const [state, setState] = useState({
        data: null,
        loading: false,
        error: null,
    });
    const updateAvatar = useCallback(async (avatarUrl) => {
        setState({ data: null, loading: true, error: null });
        try {
            const result = await userService.updateMyAvatar(avatarUrl);
            setState({ data: result, loading: false, error: null });
            return result;
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error('Failed to update avatar');
            setState({ data: null, loading: false, error });
            throw error;
        }
    }, [userService]);
    return {
        updateAvatar,
        ...state,
    };
}
/**
 * Hook to update current user's thumbnail
 * @returns Object with updateThumbnail function and mutation state
 */
export function useUpdateMyThumbnail() {
    const userService = useUserService();
    const [state, setState] = useState({
        data: null,
        loading: false,
        error: null,
    });
    const updateThumbnail = useCallback(async (thumbnailUrl) => {
        setState({ data: null, loading: true, error: null });
        try {
            const result = await userService.updateMyThumbnail(thumbnailUrl);
            setState({ data: result, loading: false, error: null });
            return result;
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error('Failed to update thumbnail');
            setState({ data: null, loading: false, error });
            throw error;
        }
    }, [userService]);
    return {
        updateThumbnail,
        ...state,
    };
}
//# sourceMappingURL=useUsersServices.js.map